/**
 * BirdieBlues Charm Builder
 * Custom drag-and-drop charm builder — no third-party app required.
 */

(function ($) {
  'use strict';

  var $root = $('#bb-charm-builder');
  if (!$root.length) return;

  /* ── State ── */
  var state = {
    step: 1,
    chain: null,     // { id, variantId, name, price, priceRaw, src }
    charms: [],      // [{ id, variantId, name, price, priceRaw, src, index, top, left, xPct, yPct, rotation }]
    charmIndex: 0
  };

  var selectedCharmIndex = null;
  var resizeTimer = null;
  var pendingCharmItem = null;
  var pendingCharmVariant = null;
  var modalReturnFocus = null;
  var charmVariantData = {};
  var baseVariantData = {};
  var chainContentBounds = {};

  try {
    charmVariantData = JSON.parse($('#bb-charm-variants').text() || '{}');
  } catch (variantDataError) {
    console.warn('BirdieBlues Charm Builder: charm variant data could not be read.', variantDataError);
  }
  try {
    baseVariantData = JSON.parse($('#bb-base-variants').text() || '{}');
  } catch (baseVariantDataError) {
    console.warn('BirdieBlues Charm Builder: base variant data could not be read.', baseVariantDataError);
  }

  /* ── Cached selectors ── */
  var $chainBg     = $('#bb-chain-bg');
  var $placeholder = $('#bb-canvas-placeholder');
  var $container   = $('#bb-charm-container');
  var $liveCount   = $('#bb-charm-count');
  var $liveTotal   = $('#bb-total-live');
  var $catalogCount = $('#bb-charm-available-count');
  var $continueBtn = $('#bb-to-step2');
  var $basePanel = $('#bb-base-panel');
  var $basePanelEmpty = $('#bb-base-panel-empty');
  var $basePanelSelected = $('#bb-base-panel-selected');
  var $baseOptionList = $('#bb-base-option-list');
  var $optionModal = $('#bb-charm-option-modal');
  var $optionList  = $('#bb-charm-option-list');
  var $confirmCharm = $('#bb-confirm-charm');

  /* Keep the desktop studio inside the visible area below the storefront
     header. The catalog becomes the only scrolling region, so the preview and
     action buttons never drift off-screen. Tall displays are capped to avoid
     an oversized, mostly empty canvas. */
  function syncWorkspaceHeight() {
    var rootElement = $root[0];
    if (!rootElement || !window.matchMedia('(min-width: 861px)').matches) {
      if (rootElement) rootElement.style.removeProperty('--bb-cb-workspace-height');
      return;
    }

    var viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    var documentTop = rootElement.getBoundingClientRect().top + window.scrollY;
    var availableHeight = Math.floor(viewportHeight - Math.max(0, documentTop));
    var workspaceHeight = Math.min(780, Math.max(580, availableHeight));
    rootElement.style.setProperty('--bb-cb-workspace-height', workspaceHeight + 'px');
  }

  /* Product photos do not all place the necklace at the same vertical point.
     Find the bottom edge of the actual product (ignoring the photo background)
     and anchor that edge inside the preview. This preserves the close-up view
     without pushing short or top-aligned necklace photos outside the canvas. */
  function detectChainContentBottom(image) {
    var sourceKey = image.currentSrc || image.src || '';
    if (chainContentBounds[sourceKey]) return chainContentBounds[sourceKey];

    var fallbackBottom = 0.86;
    try {
      var sampleWidth = Math.min(200, image.naturalWidth);
      var sampleHeight = Math.max(1, Math.round(sampleWidth * image.naturalHeight / image.naturalWidth));
      var canvas = document.createElement('canvas');
      var context = canvas.getContext('2d', { willReadFrequently: true });
      canvas.width = sampleWidth;
      canvas.height = sampleHeight;
      context.drawImage(image, 0, 0, sampleWidth, sampleHeight);

      var pixels = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
      var corners = [
        0,
        (sampleWidth - 1) * 4,
        (sampleHeight - 1) * sampleWidth * 4,
        ((sampleHeight * sampleWidth) - 1) * 4
      ];
      var background = corners.reduce(function (total, offset) {
        total.r += pixels[offset];
        total.g += pixels[offset + 1];
        total.b += pixels[offset + 2];
        total.a += pixels[offset + 3];
        return total;
      }, { r: 0, g: 0, b: 0, a: 0 });
      background.r /= corners.length;
      background.g /= corners.length;
      background.b /= corners.length;
      background.a /= corners.length;

      var minimumRowPixels = Math.max(2, Math.floor(sampleWidth * 0.006));
      var lastContentRow = -1;
      for (var y = 0; y < sampleHeight; y += 1) {
        var rowPixels = 0;
        for (var x = 0; x < sampleWidth; x += 1) {
          var index = (y * sampleWidth + x) * 4;
          var alpha = pixels[index + 3];
          var colorDistance = Math.abs(pixels[index] - background.r) +
            Math.abs(pixels[index + 1] - background.g) +
            Math.abs(pixels[index + 2] - background.b);
          var differsFromBackground = background.a < 30
            ? alpha > 30
            : alpha > 30 && (Math.abs(alpha - background.a) > 24 || colorDistance > 32);
          if (differsFromBackground) rowPixels += 1;
        }
        if (rowPixels >= minimumRowPixels) lastContentRow = y;
      }

      if (lastContentRow >= 0) {
        fallbackBottom = clamp((lastContentRow + 1) / sampleHeight, 0.28, 0.98);
      }
    } catch (cropError) {
      /* A CDN without canvas access falls back to a conservative lower crop. */
    }

    chainContentBounds[sourceKey] = fallbackBottom;
    return fallbackBottom;
  }

  function positionChainImage() {
    var image = $chainBg[0];
    var stage = $root.find('.bb-cb__chain-stage')[0];
    if (!image || !stage || !image.complete || !image.naturalWidth || !image.naturalHeight) return;

    var stageRect = stage.getBoundingClientRect();
    var imageStyle = window.getComputedStyle(image);
    var renderedHeight = parseFloat(imageStyle.height) || 0;
    if (!stageRect.width || !stageRect.height || !renderedHeight) return;

    var contentBottom = detectChainContentBottom(image);
    /* Keep the clasp in the upper half so the lower canvas remains a generous
       composition area for up to eight enlarged charms. */
    var desiredContentBottom = stageRect.height * 0.52;
    var shiftY = desiredContentBottom - (contentBottom * renderedHeight) - (stageRect.height / 2);
    shiftY = clamp(shiftY, -renderedHeight * 0.88, -renderedHeight * 0.04);
    image.style.setProperty('--bb-chain-shift-y', Math.round(shiftY) + 'px');
  }

  $chainBg.on('load.bbChainPreview', function () {
    window.requestAnimationFrame(refreshCharmLayout);
  });

  /* ════════════════════════════════════════
     STEP NAVIGATION
  ════════════════════════════════════════ */
  function goToStep(n) {
    state.step = n;

    /* Toggle full-width Step 1 layout class */
    if (n === 1) {
      $root.addClass('bb-cb--step1');
    } else {
      $root.removeClass('bb-cb--step1');
    }
    syncWorkspaceHeight();

    $root.find('.bb-cb__pane').removeClass('is-visible');
    $root.find('.bb-cb__pane[data-pane="' + n + '"]').addClass('is-visible');

    $root.find('.bb-cb__step-dot').each(function () {
      var s = parseInt($(this).data('step'));
      $(this).removeClass('is-active is-done');
      if (s === n)      $(this).addClass('is-active');
      else if (s < n)   $(this).addClass('is-done');
    });

    if (n === 2) {
      updateStep2Chip();
      updateCharmCatalogCount();
    }
    if (n === 3) buildReview();

    /* Scroll config panel to top on step change */
    $root.find('.bb-cb__config').scrollTop(0);

    /* The canvas is display:none on step 1. Recalculate percentage-based
       charm positions once it becomes measurable again. */
    if (n > 1) {
      window.requestAnimationFrame(function () {
        refreshCharmLayout();
      });
    }
    saveState();
  }

  /* ════════════════════════════════════════
     BASE (CHAIN) SELECTION — Step 1
  ════════════════════════════════════════ */
  function getBaseVariants($card) {
    var productId = String($card.data('chain-id'));
    var variants = baseVariantData[productId] || [];
    if (variants.length) return variants;

    /* Defensive fallback for products imported without variant JSON. */
    return [{
      id: String($card.data('chain-variant')),
      title: 'Standard',
      options: ['Standard'],
      optionNames: ['Option'],
      price: parseInt($card.data('chain-price-raw') || 0, 10),
      available: String($card.data('chain-available')) === 'true',
      image: $card.data('full-image') || '',
      thumb: $card.find('img').attr('src') || ''
    }];
  }

  function readableBaseVariantTitle(title, variantCount) {
    var value = String(title || '').trim();
    if (!value || value.toLowerCase() === 'default title') {
      return variantCount > 1 ? 'Option' : 'Standard';
    }
    return value;
  }

  function resetBasePanel() {
    $basePanel.removeClass('is-ready');
    $basePanelEmpty.prop('hidden', false);
    $basePanelSelected.prop('hidden', true);
    $baseOptionList.empty();
  }

  function renderBasePanel($card, preferredVariantId) {
    var variants = getBaseVariants($card);
    var availableVariants = variants.filter(function (variant) { return variant.available; });
    var selectedVariant = availableVariants.find(function (variant) {
      return String(variant.id) === String(preferredVariantId || '');
    }) || availableVariants[0];

    if (!selectedVariant) {
      resetBasePanel();
      return null;
    }

    var hasStructuredOptions = variants.length > 1 && variants.some(function (variant) {
      return Array.isArray(variant.options) && variant.options.length;
    });
    var optionHtml;

    if (hasStructuredOptions) {
      var optionNames = Array.isArray(selectedVariant.optionNames) ? selectedVariant.optionNames : [];
      var optionCount = selectedVariant.options.length;
      optionHtml = Array.from({ length: optionCount }).map(function (_, optionIndex) {
        var values = [];
        variants.forEach(function (variant) {
          var value = variant.options && variant.options[optionIndex];
          if (value != null && values.indexOf(String(value)) === -1) values.push(String(value));
        });
        var selectedValue = String(selectedVariant.options[optionIndex] || '');
        var optionName = String(optionNames[optionIndex] || ('Option ' + (optionIndex + 1)));
        var valueButtons = values.map(function (value) {
          var isSelectedValue = value === selectedValue;
          var hasAvailableValue = availableVariants.some(function (variant) {
            return String((variant.options || [])[optionIndex] || '') === value;
          });
          return '<button type="button" class="bb-cb__base-option-value' + (isSelectedValue ? ' is-selected' : '') + '"' +
            ' role="radio" aria-checked="' + (isSelectedValue ? 'true' : 'false') + '"' +
            ' data-option-index="' + optionIndex + '" data-option-value="' + escapeAttribute(value) + '"' +
            (hasAvailableValue ? '' : ' disabled aria-disabled="true"') + '>' + escapeHtml(value) + '</button>';
        }).join('');
        return '<div class="bb-cb__base-option-group">' +
          '<div class="bb-cb__base-option-group-heading"><span>' + escapeHtml(optionName) + '</span><strong>' + escapeHtml(selectedValue) + '</strong></div>' +
          '<div class="bb-cb__base-option-values" role="radiogroup" aria-label="' + escapeAttribute(optionName) + '">' + valueButtons + '</div>' +
        '</div>';
      }).join('');
      $baseOptionList.removeAttr('role aria-label');
    } else {
      optionHtml = variants.map(function (variant) {
        var isSelected = String(variant.id) === String(selectedVariant.id);
        var title = readableBaseVariantTitle(variant.title, variants.length);
        return '<button type="button" class="bb-cb__base-option' + (isSelected ? ' is-selected' : '') + '"' +
          ' role="radio" aria-checked="' + (isSelected ? 'true' : 'false') + '"' +
          ' data-base-variant-id="' + escapeAttribute(variant.id) + '"' +
          (variant.available ? '' : ' disabled aria-disabled="true"') + '>' +
            '<span class="bb-cb__base-option-copy">' +
              '<strong>' + escapeHtml(title) + '</strong>' +
              '<span>' + formatMoney(parseInt(variant.price || 0, 10)) + '</span>' +
            '</span>' +
            '<span class="bb-cb__base-option-status" aria-hidden="true">' +
              (variant.available
                ? '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m4 10 4 4 8-9"/></svg>'
                : 'Sold out') +
            '</span>' +
          '</button>';
      }).join('');
      $baseOptionList.attr({ role: 'radiogroup', 'aria-label': 'Available base variants' });
    }

    $('#bb-base-panel-name').text(String($card.data('chain-name') || 'Selected base'));
    $('#bb-base-panel-price').text(formatMoney(parseInt(selectedVariant.price || 0, 10)));
    $('#bb-base-option-count').text(availableVariants.length + (hasStructuredOptions ? ' combinations' : ' available'));
    $('#bb-base-panel-image')
      .attr('src', selectedVariant.thumb || selectedVariant.image || $card.find('img').attr('src') || '')
      .attr('alt', '');
    $baseOptionList.html(optionHtml);
    $basePanelEmpty.prop('hidden', true);
    $basePanelSelected.prop('hidden', false);
    $basePanel.addClass('is-ready');
    return selectedVariant;
  }

  function applyBaseSelection($card, variant) {
    if (!$card || !$card.length || !variant || !variant.available) return;

    var productId = String($card.data('chain-id'));
    var changedProduct = !state.chain || String(state.chain.id) !== productId;
    var fullSrc = variant.image || $card.data('full-image') || $card.find('img').attr('src') || '';

    $root.find('.bb-cb__base-card').removeClass('is-selected').attr('aria-pressed', 'false');
    $card.addClass('is-selected').attr('aria-pressed', 'true');

    state.chain = {
      id:        productId,
      variantId: String(variant.id),
      name:      String($card.data('chain-name') || ''),
      price:     formatMoney(parseInt(variant.price || 0, 10)),
      priceRaw:  parseInt(variant.price || 0, 10),
      src:       fullSrc,
      thumbSrc:  variant.thumb || $card.find('img').attr('src') || ''
    };

    if (state.chain.src) {
      $chainBg.attr('src', state.chain.src).show();
      $placeholder.hide();
      window.requestAnimationFrame(refreshCharmLayout);
    }

    /* Changing the product invalidates the previous arrangement. Changing a
       finish or size on the same product keeps the customer's charm layout. */
    if (changedProduct) clearCharms();

    $continueBtn.prop('disabled', false).css({ opacity: 1, cursor: 'pointer' });
    renderBasePanel($card, variant.id);
    updateLiveBar();
    saveState();
  }

  $root.on('click', '.bb-cb__base-card', function () {
    var $card = $(this);
    if ($card.prop('disabled') || String($card.data('chain-available')) !== 'true') {
      showToast('This base is currently sold out. Please choose an available option.');
      return;
    }

    var preferredVariantId = state.chain && String(state.chain.id) === String($card.data('chain-id'))
      ? state.chain.variantId
      : $card.data('chain-variant');
    var selectedVariant = renderBasePanel($card, preferredVariantId);
    if (!selectedVariant) {
      showToast('This base does not have an available option right now.');
      return;
    }
    applyBaseSelection($card, selectedVariant);
  });

  $root.on('click', '.bb-cb__base-option', function () {
    var $option = $(this);
    if ($option.prop('disabled')) return;
    var $card = $root.find('.bb-cb__base-card.is-selected').first();
    if (!$card.length) return;
    var variant = getBaseVariants($card).find(function (item) {
      return String(item.id) === String($option.data('base-variant-id'));
    });
    applyBaseSelection($card, variant);
  });

  $root.on('click', '.bb-cb__base-option-value', function () {
    var $value = $(this);
    if ($value.prop('disabled')) return;
    var $card = $root.find('.bb-cb__base-card.is-selected').first();
    if (!$card.length) return;

    var optionIndex = parseInt($value.attr('data-option-index'), 10);
    var optionValue = String($value.attr('data-option-value') || '');
    var variants = getBaseVariants($card);
    var availableVariants = variants.filter(function (variant) { return variant.available; });
    var currentVariant = availableVariants.find(function (variant) {
      return state.chain && String(variant.id) === String(state.chain.variantId);
    }) || availableVariants[0];
    if (!currentVariant || !Number.isFinite(optionIndex)) return;

    var desiredOptions = (currentVariant.options || []).slice();
    desiredOptions[optionIndex] = optionValue;
    var nextVariant = availableVariants.find(function (variant) {
      return desiredOptions.every(function (value, index) {
        return String((variant.options || [])[index] || '') === String(value || '');
      });
    }) || availableVariants.find(function (variant) {
      return String((variant.options || [])[optionIndex] || '') === optionValue;
    });

    if (nextVariant) applyBaseSelection($card, nextVariant);
  });

  $root.on('click', '#bb-base-tabs .bb-cb__tab', function () {
    var $tab = $(this);
    $root.find('#bb-base-tabs .bb-cb__tab').removeClass('is-active').attr('aria-selected', 'false');
    $tab.addClass('is-active').attr('aria-selected', 'true');
    var category = String($tab.data('base-cat') || 'all');
    $root.find('.bb-cb__base-card').each(function () {
      var cardCategory = String($(this).data('chain-cat') || '');
      $(this).toggle(category === 'all' || cardCategory === category);
    });
  });

  /* ════════════════════════════════════════
     STEP 2 BASE CHIP (compact summary)
  ════════════════════════════════════════ */
  function updateStep2Chip() {
    if (!state.chain) return;
    var $chip  = $('#bb-step2-base-chip');
    var $img   = $('#bb-chip-img');
    var $name  = $('#bb-chip-name');
    var $price = $('#bb-chip-price');

    $name.text(state.chain.name);
    $price.text(state.chain.price);

    if (state.chain.thumbSrc) {
      $img.attr('src', state.chain.thumbSrc).show();
    } else {
      $img.hide();
    }
    $chip.show();
  }

  /* "Change" button inside chip → back to Step 1 */
  $('#bb-chip-change').on('click', function () { goToStep(1); });
  /* "Change base" tool button in left panel */
  $('#bb-change-base').on('click', function () { goToStep(1); });

  /* ════════════════════════════════════════
     CHARM SELECTION — Step 2
  ════════════════════════════════════════ */
  $root.on('click', '.bb-cb__charm-item', function () {
    var $item = $(this);
    if ($item.prop('disabled') || String($item.data('charm-available')) !== 'true') {
      showToast('This charm is currently sold out. Please choose another.');
      return;
    }
    if (state.charms.length >= 8) {
      showToast('Maximum 8 charms per design.');
      return;
    }

    var variants = charmVariantData[String($item.data('charm-id'))] || [];
    var availableVariants = variants.filter(function (variant) { return variant.available; });
    if (!availableVariants.length) {
      showToast('This charm does not have an available option right now.');
      return;
    }

    if (String($item.data('charm-has-options')) === 'true' || variants.length > 1) {
      openCharmOptions($item, variants);
      return;
    }

    addCharmFromItem($item, availableVariants[0]);
  });

  function addCharmFromItem($item, variant) {
    if (!$item || !$item.length || !variant || !variant.available) {
      showToast('That charm option is sold out. Please choose an available option.');
      return;
    }
    if (state.charms.length >= 8) {
      showToast('Maximum 8 charms per design.');
      return;
    }

    var cw    = $container.width()  || 400;
    var ch    = $container.height() || 480;
    var idx   = state.charmIndex++;

    /* Start under the clasp, then fan later additions through the open canvas
       so all eight remain visible and easy to grab at the larger scale. */
    var placementSlot = state.charms.length % 8;
    var xSlots = [0.50, 0.32, 0.68, 0.18, 0.82, 0.40, 0.60, 0.50];
    var ySlots = [0.61, 0.63, 0.63, 0.66, 0.66, 0.82, 0.82, 0.84];
    var top  = Math.round(ch * ySlots[placementSlot] - 45);
    var left = Math.round(cw * xSlots[placementSlot] - 45);

    /* Use the high-res image (data-large) for the canvas — small thumbnails
       look blurry and the charm appears tiny within a large white border */
    var $img    = $item.find('img');
    var canvasSrc = variant.image || $img.data('large') || $img.attr('src') || '';
    var baseName = String($item.data('charm-name') || 'Charm');
    var variantTitle = String(variant.title || '');
    var displayName = variantTitle && variantTitle.toLowerCase() !== 'default title'
      ? baseName + ' — ' + variantTitle
      : baseName;

    var charm = {
      id:        String($item.data('charm-id')),
      variantId: String(variant.id),
      name:      displayName,
      price:     variant.price || formatMoney(parseInt(variant.priceRaw || 0, 10)),
      priceRaw:  parseInt(variant.priceRaw || 0, 10),
      src:       canvasSrc,
      index:     idx,
      top:       top,
      left:      left,
      xPct:      xSlots[placementSlot],
      yPct:      ySlots[placementSlot],
      rotation:  0
    };
    state.charms.push(charm);

    /* Update badge on charm grid card */
    var sameId = state.charms.filter(function (c) { return c.id === charm.id; }).length;
    $item.addClass('is-added').attr('aria-pressed', 'true');
    $item.find('.bb-cb__charm-badge').text(sameId > 1 ? sameId : '✓');

    injectCharmDiv(charm);
    updateLiveBar();
    saveState();
  }

  function openCharmOptions($item, variants) {
    pendingCharmItem = $item;
    pendingCharmVariant = variants.find(function (variant) { return variant.available; }) || null;
    modalReturnFocus = document.activeElement;

    $('#bb-charm-option-title').text($item.data('charm-name') || 'Choose an option');
    $('#bb-charm-option-image')
      .attr('src', (pendingCharmVariant && (pendingCharmVariant.thumb || pendingCharmVariant.image)) || $item.find('img').attr('src') || '')
      .attr('alt', $item.data('charm-name') || 'Charm');

    $optionList.empty();
    variants.forEach(function (variant) {
      var isSelected = pendingCharmVariant && String(variant.id) === String(pendingCharmVariant.id);
      var $option = $('<button type="button" class="bb-cb__variant-option" role="radio"></button>')
        .attr('aria-checked', isSelected ? 'true' : 'false')
        .attr('aria-disabled', variant.available ? 'false' : 'true')
        .prop('disabled', !variant.available)
        .toggleClass('is-selected', !!isSelected)
        .data('variant', variant);
      $('<span class="bb-cb__variant-title"></span>').text(variant.title || 'Default').appendTo($option);
      $('<span class="bb-cb__variant-price"></span>').text(
        variant.available ? (variant.price || formatMoney(variant.priceRaw || 0)) : 'Sold out'
      ).appendTo($option);
      $optionList.append($option);
    });

    $confirmCharm.prop('disabled', !pendingCharmVariant);
    $optionModal.prop('hidden', false);
    document.body.classList.add('bb-cb-modal-open');
    window.setTimeout(function () {
      $optionList.find('.bb-cb__variant-option:not([disabled])').first().trigger('focus');
    }, 20);
  }

  function closeCharmOptions() {
    if ($optionModal.prop('hidden')) return;
    $optionModal.prop('hidden', true);
    document.body.classList.remove('bb-cb-modal-open');
    pendingCharmItem = null;
    pendingCharmVariant = null;
    if (modalReturnFocus && typeof modalReturnFocus.focus === 'function') modalReturnFocus.focus();
    modalReturnFocus = null;
  }

  $optionList.on('click', '.bb-cb__variant-option', function () {
    var $option = $(this);
    if ($option.prop('disabled')) return;
    pendingCharmVariant = $option.data('variant');
    $optionList.find('.bb-cb__variant-option').removeClass('is-selected').attr('aria-checked', 'false');
    $option.addClass('is-selected').attr('aria-checked', 'true');
    if (pendingCharmVariant && (pendingCharmVariant.thumb || pendingCharmVariant.image)) {
      $('#bb-charm-option-image').attr('src', pendingCharmVariant.thumb || pendingCharmVariant.image);
    }
    $confirmCharm.prop('disabled', false);
  });

  $confirmCharm.on('click', function () {
    var $item = pendingCharmItem;
    var variant = pendingCharmVariant;
    if (!variant || !variant.available) {
      showToast('Please choose an available charm option.');
      return;
    }
    closeCharmOptions();
    addCharmFromItem($item, variant);
  });

  $('[data-bb-modal-close]').on('click', closeCharmOptions);
  $(document).on('keydown.bbCharmModal', function (event) {
    if ($optionModal.prop('hidden')) return;
    if (event.key === 'Escape') {
      closeCharmOptions();
      return;
    }
    if (event.key === 'Tab') {
      var focusable = $optionModal[0].querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  /* ── Inject a native Pointer Events charm ── */
  function injectCharmDiv(charm) {
    var html =
      '<div class="bb-charm is-entering"' +
          ' data-index="' + charm.index + '"' +
          ' data-product-id="' + escapeAttribute(charm.id) + '"' +
          ' data-variant-id="' + escapeAttribute(charm.variantId) + '"' +
          ' tabindex="0" role="group" aria-selected="false"' +
          ' aria-label="' + escapeAttribute(charm.name) + ' charm. Drag to position, use the blue handle to rotate, or press Delete to remove."' +
          ' style="position:absolute;top:' + (charm.top || 0) + 'px;left:' + (charm.left || 0) + 'px;' +
                  '--bb-charm-rotation:' + (charm.rotation || 0) + 'deg;touch-action:none;">' +
        '<div class="bb-charm__img-wrap">' +
          '<img src="' + escapeAttribute(charm.src) + '" alt="" draggable="false" width="180" height="180">' +
        '</div>' +
        '<button type="button" class="bb-charm__rotate-handle" aria-label="Drag to rotate ' + escapeAttribute(charm.name) + '" title="Drag to rotate">' +
          '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36"/><polyline points="21 3 21 9 15 9"/></svg>' +
        '</button>' +
        '<div class="bb-charm__controls" aria-label="Charm actions">' +
          '<button type="button" class="bb-charm__btn bb-charm__remove" title="Remove" aria-label="Remove ' + escapeAttribute(charm.name) + '">×</button>' +
        '</div>' +
      '</div>';

    var $charm = $(html);
    $container.append($charm);
    positionCharm($charm, charm);
    bindCharmGestures($charm, charm);
    selectCharm($charm);

    window.setTimeout(function () { $charm.removeClass('is-entering'); }, 260);
  }

  function bindCharmGestures($charm, charm) {
    var element = $charm[0];
    var rotateHandle = element.querySelector('.bb-charm__rotate-handle');

    element.addEventListener('pointerdown', function (event) {
      if (event.button !== undefined && event.button !== 0) return;
      if (event.isPrimary === false) return;
      if (event.target.closest('.bb-charm__controls, .bb-charm__rotate-handle')) return;

      event.preventDefault();
      selectCharm($charm);

      var pointerId = event.pointerId;
      var startX = event.clientX;
      var startY = event.clientY;
      var startLeft = parseFloat(element.style.left) || 0;
      var startTop = parseFloat(element.style.top) || 0;
      var nextLeft = startLeft;
      var nextTop = startTop;
      var frame = null;

      element.classList.add('is-dragging');
      element.setPointerCapture(pointerId);

      function drawDrag() {
        frame = null;
        element.style.left = nextLeft + 'px';
        element.style.top = nextTop + 'px';
        updateCharmEdgeState($charm);
      }

      function onMove(moveEvent) {
        if (moveEvent.pointerId !== pointerId) return;
        var bounds = getCharmBounds(element);
        nextLeft = clamp(startLeft + moveEvent.clientX - startX, 0, bounds.maxLeft);
        nextTop = clamp(startTop + moveEvent.clientY - startY, 0, bounds.maxTop);
        if (!frame) frame = window.requestAnimationFrame(drawDrag);
      }

      function finishDrag(endEvent) {
        if (endEvent.pointerId !== pointerId) return;
        if (frame) {
          window.cancelAnimationFrame(frame);
          drawDrag();
        }
        element.classList.remove('is-dragging');
        if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId);
        element.removeEventListener('pointermove', onMove);
        element.removeEventListener('pointerup', finishDrag);
        element.removeEventListener('pointercancel', finishDrag);
        storeCharmPosition($charm, charm);
        saveState();
      }

      element.addEventListener('pointermove', onMove);
      element.addEventListener('pointerup', finishDrag);
      element.addEventListener('pointercancel', finishDrag);
    });

    rotateHandle.addEventListener('pointerdown', function (event) {
      if (event.button !== undefined && event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      selectCharm($charm);

      var pointerId = event.pointerId;
      var rect = element.getBoundingClientRect();
      var centerX = rect.left + rect.width / 2;
      var centerY = rect.top + rect.height / 2;
      var startAngle = Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180 / Math.PI;
      var startRotation = Number(charm.rotation) || 0;
      var nextRotation = startRotation;
      var frame = null;

      rotateHandle.setPointerCapture(pointerId);

      function drawRotation() {
        frame = null;
        charm.rotation = normalizeAngle(nextRotation);
        element.style.setProperty('--bb-charm-rotation', charm.rotation + 'deg');
      }

      function onRotate(moveEvent) {
        if (moveEvent.pointerId !== pointerId) return;
        var pointerAngle = Math.atan2(moveEvent.clientY - centerY, moveEvent.clientX - centerX) * 180 / Math.PI;
        nextRotation = startRotation + pointerAngle - startAngle;
        if (!frame) frame = window.requestAnimationFrame(drawRotation);
      }

      function finishRotation(endEvent) {
        if (endEvent.pointerId !== pointerId) return;
        if (frame) {
          window.cancelAnimationFrame(frame);
          drawRotation();
        }
        if (rotateHandle.hasPointerCapture(pointerId)) rotateHandle.releasePointerCapture(pointerId);
        rotateHandle.removeEventListener('pointermove', onRotate);
        rotateHandle.removeEventListener('pointerup', finishRotation);
        rotateHandle.removeEventListener('pointercancel', finishRotation);
        saveState();
      }

      rotateHandle.addEventListener('pointermove', onRotate);
      rotateHandle.addEventListener('pointerup', finishRotation);
      rotateHandle.addEventListener('pointercancel', finishRotation);
    });
  }

  function selectCharm($div) {
    selectedCharmIndex = parseInt($div.data('index'), 10);
    $container.find('.bb-charm').removeClass('is-selected').attr('aria-selected', 'false');
    $div.addClass('is-selected').attr('aria-selected', 'true');
  }

  function clearCharmSelection() {
    selectedCharmIndex = null;
    $container.find('.bb-charm').removeClass('is-selected').attr('aria-selected', 'false');
  }

  $('#bb-charm-visual').on('pointerdown', function (event) {
    if (!$(event.target).closest('.bb-charm').length) clearCharmSelection();
  });

  /* ── Keyboard rotation (the blue handle is the visual rotation control) ── */
  function applyRotation($div, delta) {
    var idx = parseInt($div.data('index'), 10);
    var c   = findCharm(idx);
    if (c) c.rotation = normalizeAngle((Number(c.rotation) || 0) + delta);
    var rot = c ? c.rotation : delta;
    $div[0].style.setProperty('--bb-charm-rotation', rot + 'deg');
    selectCharm($div);
    saveState();
  }

  $root.on('keydown', '.bb-charm', function (event) {
    if ($(event.target).is('button')) return;
    var $div = $(this);
    var amount = event.shiftKey ? 10 : 2;
    var handled = true;

    switch (event.key) {
      case 'ArrowLeft':  moveCharmBy($div, -amount, 0); break;
      case 'ArrowRight': moveCharmBy($div, amount, 0); break;
      case 'ArrowUp':    moveCharmBy($div, 0, -amount); break;
      case 'ArrowDown':  moveCharmBy($div, 0, amount); break;
      case '[':          applyRotation($div, event.shiftKey ? -15 : -5); break;
      case ']':          applyRotation($div, event.shiftKey ? 15 : 5); break;
      case 'Delete':
      case 'Backspace':
        $div.find('.bb-charm__remove').trigger('click');
        break;
      default: handled = false;
    }

    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  });

  function moveCharmBy($div, deltaX, deltaY) {
    var charm = findCharm(parseInt($div.data('index'), 10));
    if (!charm) return;
    var element = $div[0];
    var bounds = getCharmBounds(element);
    element.style.left = clamp((parseFloat(element.style.left) || 0) + deltaX, 0, bounds.maxLeft) + 'px';
    element.style.top = clamp((parseFloat(element.style.top) || 0) + deltaY, 0, bounds.maxTop) + 'px';
    storeCharmPosition($div, charm);
    selectCharm($div);
    saveState();
  }

  /* ── Remove charm from canvas ── */
  $root.on('click', '.bb-charm__remove', function (e) {
    e.stopPropagation();
    var $div = $(this).closest('.bb-charm');
    var idx  = parseInt($div.data('index'));
    var pid  = String($div.data('product-id'));
    removeCharmFromState(idx, pid);
    $div.remove();
    if (selectedCharmIndex === idx) selectedCharmIndex = null;
    updateLiveBar();
    saveState();
  });

  function removeCharmFromState(idx, pid) {
    state.charms = state.charms.filter(function (c) { return c.index !== idx; });
    var remaining = state.charms.filter(function (c) { return c.id === pid; }).length;
    var $gi = $root.find('.bb-cb__charm-item[data-charm-id="' + pid + '"]');
    if (remaining === 0) {
      $gi.removeClass('is-added').attr('aria-pressed', 'false');
      $gi.find('.bb-cb__charm-badge').text('');
    } else {
      $gi.find('.bb-cb__charm-badge').text(remaining > 1 ? remaining : '✓');
    }
  }

  function clearCharms() {
    state.charms    = [];
    state.charmIndex = 0;
    $container.empty();
    selectedCharmIndex = null;
    $root.find('.bb-cb__charm-item').removeClass('is-added').attr('aria-pressed', 'false');
    $root.find('.bb-cb__charm-badge').text('');
    updateLiveBar();
  }

  /* ── Charm category tabs ── */
  $root.on('click', '.bb-cb__charm-tab', function () {
    var $tab = $(this);
    var cat  = $tab.data('charm-cat');
    $root.find('.bb-cb__charm-tab').removeClass('is-active');
    $tab.addClass('is-active');
    $root.find('.bb-cb__charm-item').each(function () {
      $(this).toggle(cat === 'all' || charmMatchesCategory(this, cat));
    });
    updateCharmCatalogCount();
  });

  function charmMatchesCategory(item, category) {
    var categories = String($(item).attr('data-charm-cat') || '').split(/\s+/);
    return categories.indexOf(String(category)) !== -1;
  }

  function updateCharmTabAvailability() {
    var $items = $root.find('.bb-cb__charm-item');
    $root.find('.bb-cb__charm-tab').each(function () {
      var $tab = $(this);
      var category = String($tab.data('charm-cat'));
      var hasMatches = category === 'all' || $items.filter(function () {
        return charmMatchesCategory(this, category);
      }).length > 0;
      $tab.toggle(hasMatches).attr('aria-hidden', hasMatches ? 'false' : 'true');
    });
  }

  function updateCharmCatalogCount() {
    var count = $root.find('.bb-cb__charm-item').filter(function () {
      return this.style.display !== 'none' && String($(this).data('charm-available')) === 'true';
    }).length;
    $catalogCount.text(count + ' available style' + (count === 1 ? '' : 's'));
  }

  /* ════════════════════════════════════════
     LIVE BAR
  ════════════════════════════════════════ */
  function updateLiveBar() {
    var cnt      = state.charms.length;
    var chainRaw = state.chain ? (state.chain.priceRaw || 0) : 0;
    var total    = chainRaw + state.charms.reduce(function (s, c) { return s + c.priceRaw; }, 0);
    $liveCount.text(cnt + ' Charm' + (cnt !== 1 ? 's' : ''));
    $liveTotal.text(formatMoney(total));
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* ════════════════════════════════════════
     REVIEW — Step 3
  ════════════════════════════════════════ */
  function buildReview() {
    if (state.chain) {
      $('#bb-review-base-name').text(state.chain.name);
      $('#bb-review-base-price').text(state.chain.price);
      $('#bb-review-base-img-wrap').html(
        state.chain.thumbSrc
          ? '<img src="' + escapeAttribute(state.chain.thumbSrc) + '" alt="" width="168" height="168">'
          : ''
      );
    }
    $('#bb-review-charm-count').text(state.charms.length);

    var $list = $('#bb-selected-charms-list');
    if (state.charms.length === 0) {
      $list.html('<p class="bb-cb__review-empty">No charms added yet — <a href="#" id="bb-go-add-charms">go back to add some</a>.</p>');
    } else {
      $list.html(state.charms.map(function (c) {
        return '<div class="bb-cb__selected-charm" data-charm-remove="' + c.index + '" data-charm-pid="' + c.id + '">' +
          (c.src ? '<img src="' + escapeAttribute(c.src) + '" alt="" width="56" height="56">' : '') +
          '<span>' + escapeHtml(c.name) + '</span>' +
          '<button type="button" class="bb-cb__selected-charm-remove" title="Remove" aria-label="Remove ' + escapeAttribute(c.name) + '">×</button>' +
        '</div>';
      }).join(''));
    }

    var chainRaw = state.chain ? state.chain.priceRaw : 0;
    var charmRaw = state.charms.reduce(function (s, c) { return s + c.priceRaw; }, 0);
    $('#bb-grand-total').text(formatMoney(chainRaw + charmRaw));
  }

  /* "Go add charms" shortcut from empty state */
  $root.on('click', '#bb-go-add-charms', function (e) { e.preventDefault(); goToStep(2); });

  /* Remove charm from review list */
  $root.on('click', '.bb-cb__selected-charm-remove', function () {
    var $row = $(this).closest('.bb-cb__selected-charm');
    var idx  = parseInt($row.data('charm-remove'));
    var pid  = String($row.data('charm-pid'));
    /* Remove from canvas too */
    $container.find('.bb-charm[data-index="' + idx + '"]').remove();
    removeCharmFromState(idx, pid);
    updateLiveBar();
    buildReview();
  });

  /* ════════════════════════════════════════
     ADD TO CART
  ════════════════════════════════════════ */
  $('#bb-add-to-cart').on('click', function () {
    if (!state.chain) {
      showToast('Please select a base first.');
      goToStep(1);
      return;
    }

    var $btn = $(this);
    $btn.addClass('is-loading').html('<span class="bb-cb__spinner"></span>&nbsp;Adding to Cart…');
    postToCart($btn);
  });

  var CART_BTN_HTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" width="15" height="15"><path d="M6 8h12l-1 12H7L6 8z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg> Add to Cart';

  function postToCart($btn) {
    /* ── Validate variant IDs before sending ── */
    var chainId = parseInt(state.chain.variantId, 10);
    if (!chainId || isNaN(chainId)) {
      $btn.removeClass('is-loading').html(CART_BTN_HTML);
      showToast('Could not find the base product. Please refresh and try again.');
      console.error('[BirdieBlues] Missing chain variantId:', state.chain);
      return;
    }

    var selectableBaseVariant = (baseVariantData[String(state.chain.id)] || []).find(function (variant) {
      return variant.available && String(variant.id) === String(chainId);
    });
    if (!selectableBaseVariant) {
      $btn.removeClass('is-loading').html(CART_BTN_HTML);
      showToast('That base option is no longer available. Please choose another.');
      clearState();
      goToStep(1);
      return;
    }

    var items = [];

    /* Base chain */
    items.push({
      id:         chainId,
      quantity:   1,
      properties: { 'Design': state.chain.name, 'Charm Count': String(state.charms.length) }
    });

    /* Charms — validate each variant ID */
    var badCharm = null;
    state.charms.forEach(function (c, i) {
      var vid = parseInt(c.variantId, 10);
      var selectableCharmVariant = (charmVariantData[String(c.id)] || []).find(function (variant) {
        return variant.available && String(variant.id) === String(vid);
      });
      if (!vid || isNaN(vid) || !selectableCharmVariant) { badCharm = c.name; return; }
      items.push({
        id:         vid,
        quantity:   1,
        properties: {
          'Charm': c.name,
          'Position': i + 1,
          'Base': state.chain.name,
          'Rotation': Math.round(Number(c.rotation) || 0) + '°',
          'Placement': Math.round((Number(c.xPct) || 0.5) * 100) + '% × ' + Math.round((Number(c.yPct) || 0.5) * 100) + '%'
        }
      });
    });

    if (badCharm) {
      $btn.removeClass('is-loading').html(CART_BTN_HTML);
      showToast('Could not add ' + badCharm + '. Please remove it and choose the charm again.');
      console.error('[BirdieBlues] Missing charm variantId:', badCharm);
      return;
    }

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify({ items: items })
    })
    .then(function (res) {
      return res.text().then(function (text) {
        var data;
        try { data = JSON.parse(text); } catch (e) { data = {}; }
        if (!res.ok) {
          var msg = (data && (data.description || data.message)) || ('Server error ' + res.status);
          console.error('[BirdieBlues] /cart/add.js failed:', res.status, text);
          throw new Error(msg);
        }
        return data;
      });
    })
    .then(function () {
      /* ── Success: update button, open cart, then go to cart page ── */
      $btn.removeClass('is-loading').html('✓ Added! Taking you to cart…');
      clearState();

      /* Try to open Dawn cart drawer (best-effort) */
      tryOpenCartDrawer();

      /* Navigate to /cart after a short delay so the drawer has time to open */
      setTimeout(function () {
        window.location.href = '/cart';
      }, 900);
    })
    .catch(function (err) {
      $btn.removeClass('is-loading').html(CART_BTN_HTML);
      var msg = (err && err.message) ? err.message : 'Could not add to cart — please try again.';
      showToast(msg);
      console.error('[BirdieBlues] Cart add error:', err);
    });
  }

  /* ── Try to open Dawn cart drawer (best-effort; /cart is the reliable fallback) ── */
  function tryOpenCartDrawer() {
    var root      = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var sectionsUrl = root + '?sections=cart-drawer,cart-icon-bubble';

    fetch(sectionsUrl, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var parser = new DOMParser();

        /* ── 1. Update cart icon bubble (item count in header) ── */
        if (data['cart-icon-bubble']) {
          var bubbleDoc = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
          var newBubble = bubbleDoc.querySelector('#cart-icon-bubble');
          var oldBubble = document.getElementById('cart-icon-bubble');
          if (newBubble && oldBubble) {
            oldBubble.innerHTML = newBubble.innerHTML;
            /* Ensure it's visible */
            oldBubble.closest('.cart-count-bubble, [class*="cart"]') &&
              oldBubble.closest('.cart-count-bubble, [class*="cart"]').removeAttribute('hidden');
          }
        }

        /* ── 2. Refresh cart drawer contents ── */
        var drawerEl = document.querySelector('cart-drawer');
        if (data['cart-drawer'] && drawerEl) {
          var drawerDoc = parser.parseFromString(data['cart-drawer'], 'text/html');
          var newDrawer = drawerDoc.querySelector('cart-drawer');
          if (newDrawer) {
            /* Replace only the inner HTML so the custom element class instance
               (registered via customElements.define) stays bound to the same
               DOM node. Dawn's open() and close() methods live on this node. */
            drawerEl.innerHTML = newDrawer.innerHTML;
          }
        }

        /* ── 3. Open the drawer ── */
        triggerCartOpen(drawerEl);
      })
      .catch(function () {
        /* Sections fetch failed — still try to open with whatever's in the DOM */
        triggerCartOpen(document.querySelector('cart-drawer'));
      });
  }

  function triggerCartOpen(drawerEl) {
    if (drawerEl) {
      if (typeof drawerEl.open === 'function') {
        /* Dawn standard: custom element exposes open() */
        drawerEl.open();
        return;
      }
      /* Older / customised Dawn: manually show drawer overlay */
      drawerEl.removeAttribute('hidden');
      drawerEl.setAttribute('aria-expanded', 'true');
      var overlay = document.getElementById('CartDrawer-Overlay') ||
                    document.querySelector('[id*="CartDrawer"] .drawer__overlay');
      if (overlay) { overlay.removeAttribute('hidden'); overlay.classList.add('active'); }
      /* Try focusing first focusable element inside */
      var firstFocusable = drawerEl.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) firstFocusable.focus();
      return;
    }

    /* No cart-drawer element — click the header cart icon so the theme handles it */
    var cartLink = document.querySelector('a[href="/cart"].header__icon, .header__icon--cart a, a[href="/cart"][class*="icon"]');
    if (cartLink) { cartLink.click(); return; }

    /* Absolute fallback */
    window.location.href = '/cart';
  }

  /* ════════════════════════════════════════
     STEP BUTTON WIRING
  ════════════════════════════════════════ */
  $('#bb-to-step2').on('click', function () {
    if (!$(this).prop('disabled')) goToStep(2);
  });
  $('#bb-to-step3').on('click',   function () { goToStep(3); });
  $('#bb-back-step1').on('click', function () { goToStep(1); });
  $('#bb-back-step2').on('click', function () { goToStep(2); });

  /* ════════════════════════════════════════
     RESET
  ════════════════════════════════════════ */
  $('#bb-reset').on('click', function () {
    if (!confirm('Start over? This will clear your current design.')) return;
    clearCharms();
    state.chain = null;
    $chainBg.hide().attr('src', '');
    $placeholder.show();
    $root.find('.bb-cb__base-card').removeClass('is-selected').attr('aria-pressed', 'false');
    $continueBtn.prop('disabled', true).css({ opacity: 0.45, cursor: 'not-allowed' });
    resetBasePanel();
    clearState();
    goToStep(1);
  });

  /* ════════════════════════════════════════
     STATE PERSISTENCE (localStorage)
  ════════════════════════════════════════ */
  function saveState() {
    try {
      localStorage.setItem('bb_charm_state', JSON.stringify({
        step:   state.step,
        chain:  state.chain,
        charms: state.charms
      }));
    } catch (e) {}
  }

  function clearState() {
    try { localStorage.removeItem('bb_charm_state'); } catch (e) {}
  }

  function restoreState() {
    try {
      var raw = localStorage.getItem('bb_charm_state');
      if (!raw) return;
      var saved = JSON.parse(raw);
      if (!saved || !saved.chain) return;

      /* Restore base */
      var $savedBase = $root.find('.bb-cb__base-card[data-chain-id="' + saved.chain.id + '"]');
      if (!$savedBase.length || $savedBase.prop('disabled') || String($savedBase.data('chain-available')) !== 'true') {
        clearState();
        return;
      }
      state.chain = saved.chain;
      state.chain.name = String(state.chain.name || '').replace(/\s*\(Charm Builder\)\s*$/i, '');
      var availableBaseVariants = (baseVariantData[String(saved.chain.id)] || []).filter(function (variant) { return variant.available; });
      var restoredBaseVariant = availableBaseVariants.find(function (variant) {
        return String(variant.id) === String(saved.chain.variantId);
      }) || availableBaseVariants[0];
      if (!restoredBaseVariant) {
        clearState();
        return;
      }
      state.chain.variantId = String(restoredBaseVariant.id);
      state.chain.priceRaw = parseInt(restoredBaseVariant.price || 0, 10);
      state.chain.price = formatMoney(state.chain.priceRaw);
      if (restoredBaseVariant.image) state.chain.src = restoredBaseVariant.image;
      if (restoredBaseVariant.thumb) state.chain.thumbSrc = restoredBaseVariant.thumb;
      if (state.chain.src) {
        $chainBg.attr('src', state.chain.src).show();
        $placeholder.hide();
      }
      $savedBase.addClass('is-selected').attr('aria-pressed', 'true');
      $continueBtn.prop('disabled', false).css({ opacity: 1, cursor: 'pointer' });
      renderBasePanel($savedBase, state.chain.variantId);

      /* Restore charms */
      var skippedSavedCharm = false;
      (saved.charms || []).forEach(function (c) {
        var $gi = $root.find('.bb-cb__charm-item[data-charm-id="' + c.id + '"]');
        if (!$gi.length || $gi.prop('disabled') || String($gi.data('charm-available')) !== 'true') return;
        var availableCharmVariants = (charmVariantData[String(c.id)] || []).filter(function (variant) {
          return variant.available;
        });
        var restoredCharmVariant = availableCharmVariants.find(function (variant) {
          return String(variant.id) === String(c.variantId);
        });
        if (!restoredCharmVariant && String($gi.data('charm-has-options')) !== 'true') {
          restoredCharmVariant = availableCharmVariants[0];
        }
        if (!restoredCharmVariant) {
          skippedSavedCharm = true;
          return;
        }
        var restoredBaseName = String($gi.data('charm-name') || 'Charm');
        var restoredVariantTitle = String(restoredCharmVariant.title || '');
        c.variantId = String(restoredCharmVariant.id);
        c.priceRaw = parseInt(restoredCharmVariant.priceRaw || 0, 10);
        c.price = restoredCharmVariant.price || formatMoney(c.priceRaw);
        c.src = restoredCharmVariant.image || c.src;
        c.name = restoredVariantTitle && restoredVariantTitle.toLowerCase() !== 'default title'
          ? restoredBaseName + ' — ' + restoredVariantTitle
          : restoredBaseName;
        state.charms.push(c);
        if (state.charmIndex <= c.index) state.charmIndex = c.index + 1;
        injectCharmDiv(c);
        $gi.addClass('is-added').attr('aria-pressed', 'true');
        var n = state.charms.filter(function (x) { return x.id === c.id; }).length;
        $gi.find('.bb-cb__charm-badge').text(n > 1 ? n : '✓');
      });

      updateLiveBar();
      if (skippedSavedCharm) {
        window.setTimeout(function () {
          showToast('One or more saved charm options are no longer available. Please choose them again.');
        }, 250);
      }

      /* Resume the last completed step without skipping review. */
      if (saved.step && saved.step > 1) goToStep(Math.min(3, saved.step));
    } catch (e) {
      console.warn('[BirdieBlues] Could not restore saved builder state:', e);
      clearState();
    }
  }

  /* ════════════════════════════════════════
     UTILITIES
  ════════════════════════════════════════ */
  function positionCharm($div, charm) {
    var element = $div[0];
    var cw = $container.width();
    var ch = $container.height();
    if (!cw || !ch) return;

    var width = element.offsetWidth || 90;
    var height = element.offsetHeight || 90;
    var left = Number(charm.left) || 0;
    var top = Number(charm.top) || 0;

    if (Number.isFinite(Number(charm.xPct)) && Number.isFinite(Number(charm.yPct))) {
      left = Number(charm.xPct) * cw - width / 2;
      top = Number(charm.yPct) * ch - height / 2;
    }

    var bounds = getCharmBounds(element);
    left = clamp(left, 0, bounds.maxLeft);
    top = clamp(top, 0, bounds.maxTop);
    element.style.left = left + 'px';
    element.style.top = top + 'px';
    element.style.setProperty('--bb-charm-rotation', (Number(charm.rotation) || 0) + 'deg');
    storeCharmPosition($div, charm);
  }

  function storeCharmPosition($div, charm) {
    var element = $div[0];
    var cw = $container.width();
    var ch = $container.height();
    charm.left = parseFloat(element.style.left) || 0;
    charm.top = parseFloat(element.style.top) || 0;
    if (cw && ch) {
      charm.xPct = clamp((charm.left + element.offsetWidth / 2) / cw, 0, 1);
      charm.yPct = clamp((charm.top + element.offsetHeight / 2) / ch, 0, 1);
    }
    updateCharmEdgeState($div);
  }

  function getCharmBounds(element) {
    var width = element.offsetWidth || 90;
    var height = element.offsetHeight || 90;
    var canvasWidth = $container.width() || 0;
    var canvasHeight = $container.height() || 0;
    var bottomOverhang = Math.min(18, height * 0.2);
    return {
      maxLeft: Math.max(0, canvasWidth - width),
      /* A small controlled overhang lets the pendant sit below the clasp
         without clipping the charm or losing its drag target. */
      maxTop: Math.max(0, canvasHeight - height + bottomOverhang)
    };
  }

  function updateCharmEdgeState($div) {
    if (!$div || !$div.length) return;
    var element = $div[0];
    var canvasHeight = $container.height() || 0;
    var centerY = (parseFloat(element.style.top) || 0) + element.offsetHeight / 2;
    $div.toggleClass('is-near-bottom', canvasHeight > 0 && centerY > canvasHeight - 74);
  }

  function refreshCharmLayout() {
    positionChainImage();
    state.charms.forEach(function (charm) {
      var $div = $container.find('.bb-charm[data-index="' + charm.index + '"]');
      if ($div.length) positionCharm($div, charm);
    });
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(Number(value) || 0, min), max);
  }

  function normalizeAngle(angle) {
    var normalized = Number(angle) || 0;
    while (normalized > 180) normalized -= 360;
    while (normalized <= -180) normalized += 360;
    return Math.round(normalized * 10) / 10;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function escapeAttribute(value) {
    return escapeHtml(value);
  }

  function findCharm(idx) {
    return state.charms.find(function (c) { return c.index === idx; }) || null;
  }

  function showToast(msg) {
    var $t = $('<div class="bb-cb__toast" role="status" aria-live="polite"></div>').text(msg);
    $('body').append($t);
    setTimeout(function () { $t.addClass('is-visible'); }, 10);
    setTimeout(function () {
      $t.removeClass('is-visible');
      setTimeout(function () { $t.remove(); }, 300);
    }, 2800);
  }

  /* ════════════════════════════════════════
     INIT
  ════════════════════════════════════════ */
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      syncWorkspaceHeight();
      refreshCharmLayout();
    }, 120);
  });
  syncWorkspaceHeight();
  $continueBtn.prop('disabled', true).css({ opacity: 0.45, cursor: 'not-allowed' });
  resetBasePanel();
  updateCharmTabAvailability();
  updateCharmCatalogCount();
  updateLiveBar();
  restoreState();

})(jQuery);
